import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * Presentational status pill. Maps a semantic `tone` to the design-token
 * status colors — never hardcodes a hex value. Entities decide which tone a
 * given status maps to; this component knows nothing about orders or couriers.
 */
const statusBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        info: 'border-info/30 bg-info/15 text-info',
        pending: 'border-status-pending/30 bg-status-pending/15 text-status-pending',
        active: 'border-status-active/30 bg-status-active/15 text-status-active',
        success: 'border-status-success/30 bg-status-success/15 text-status-success',
        danger: 'border-status-danger/30 bg-status-danger/15 text-status-danger',
        neutral: 'border-status-neutral/30 bg-status-neutral/15 text-status-neutral',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
);

export type StatusTone = NonNullable<VariantProps<typeof statusBadgeVariants>['tone']>;

interface StatusBadgeProps extends VariantProps<typeof statusBadgeVariants> {
  children: React.ReactNode;
  className?: string;
}

export function StatusBadge({ tone, children, className }: StatusBadgeProps) {
  return (
    <span className={cn(statusBadgeVariants({ tone }), className)}>
      <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />
      {children}
    </span>
  );
}

export { statusBadgeVariants };
